<template lang="html">
  <div class="discover-capsules-container">
    <i
        class="feature-tour-icon pe-7s-info float-right"
        @click="startTour()"
        data-v-step="0"
      />
    <div class="filters-wrapper">
      <div
        class="sidebar-filter-btn"
        :class="{ active: filter === 'fresh' }"
        @click="changeFilter('fresh')"
      >
        <IconLeaf /><span class="text">Fresh</span>
      </div>

      <div
        class="sidebar-filter-btn"
        :class="{ active: filter === 'hot' }"
        @click="changeFilter('hot')"
      >
        <IconChili /><span class="text">Hot</span>
      </div>
    </div>

    <v-tour
      name="myTour"
      :steps="steps"
      :callbacks="myCallbacks"
      :options="featureTourOptions"
    ></v-tour>

    <div
      v-for="capsule in capsules"
      :key="capsule.id"
      class="capsule-wrapper"
    >
      <CapsuleHomeWrapper :capsule="capsule" />
    </div>

    <div class="d-flex justify-content-center mb-5">
      <b-button
        variant="light"
        v-if="hasMoreCapsules"
        @click="loadMoreCapsules()"
      >
        <span>{{ loadingButtonText }}</span>
        <b-spinner small class="mr-1 ml-2" v-if="loadingData"></b-spinner>
      </b-button>
    </div>
  </div>
</template>

<script>
import CapsuleHomeWrapper from "@/components/capsule/CapsuleHomeWrapper";

import IconChili from "@/components/discover/IconChili";
import IconLeaf from "@/components/discover/IconLeaf";

import cookies from "js-cookie";
import { mapActions, mapGetters } from "vuex";
import { bus } from "@/main";

export default {
  components: {
    CapsuleHomeWrapper,
    IconChili,
    IconLeaf,
  },

  created() {
    this.requestCapsules(1);
  },

  mounted() {
    window.analytics.page("Discover Capsules");
    window.addEventListener("scroll", this.infiniteScroll);

    bus.$on("featureTourSwipingCapsule", (layer) => {
      this.featureTourMoveToNextStep(1);
    });

    bus.$on("featureTourOpenProductModal", () => {
      this.$tours["myTour"].finish();
    });
  },

  destroy() {
    window.removeEventListener("scroll", this.infiniteScroll);
    this.resetDiscoverCapsules();
  },

  data() {
    return {
      filter: "fresh",
      loadingData: true,
      featureTourOptions: {
        labels: {
          buttonSkip: "End",
          buttonPrevious: "Prev",
          buttonNext: "Next",
          buttonStop: "Finish",
        },
      },
      steps: [
        {
          target: '[data-v-step="0"]', // We're using document.querySelector() under the hood
          header: {
            title: "Welcome! 👋",
          },
          content: `Learn the <strong style="color: #81e5cd">basics</strong>`,
          params: {
            highlight: true,
            enableScrolling: false,
            placement: "left",
          },
        },
        {
          target: '[data-v-step="1"]',
          content: `<strong style="color: #81e5cd">Swipe</strong> to create outfits`,
          params: {
            highlight: true,
            enableScrolling: false,
          },
        },
        {
          target: '[data-v-step="2"]',
          content: `<strong style="color: #81e5cd">Click</strong> to open product`,
          params: {
            highlight: true,
            enableScrolling: false,
          },
        },
        {
          target: '[data-v-step="3"]',
          content: `<strong style="color: #81e5cd">Open</strong> capsule to stitch outfits`,
          params: {
            highlight: true,
            enableScrolling: false,
            placement: "left",
          },
        },
      ],
      myCallbacks: {
        onNextStep: this.featureTourCallback,
        onSkip: this.finishTour,
        onFinish: this.finishTour,
      },
    };
  },

  computed: {
    ...mapGetters(["currentUser", "discoverCapsules"]),

    loadingButtonText() {
      return this.loadingData ? "Loading Capsules" : "Load more capsules";
    },

    capsules() {
      return this.discoverCapsules.all;
    },

    metaData() {
      return this.discoverCapsules["metaData"];
    },

    hasMoreCapsules() {
      return this.metaData.length === 0 || this.metaData.next_page !== null;
    },

    coachmarksCookie() {
      return cookies.get("_unstitched_home_coachmarks");
    },

    hasRunTour() {
      return (
        this.coachmarksCookie == "true" ||
        (this.currentUser && this.currentUser.home_coachmarks)
      );
    },
  },

  methods: {
    ...mapActions([
      "fetchDiscoverCapsules",
      "fetchVotes",
      "handleFinishFeatureTour",
      "resetDiscoverCapsules",
    ]),

    changeFilter(filter) {
      this.filter = filter;
    },

    infiniteScroll() {
      if (
        window.innerHeight + window.scrollY >= document.body.offsetHeight &&
        this.hasMoreCapsules
      ) {
        this.loadMoreCapsules();
      }
    },

    loadMoreCapsules() {
      if (!this.loadingData && this.hasMoreCapsules) {
        this.requestCapsules(this.metaData.next_page);
      }
    },

    requestCapsules(page) {
      const payload = {
        filter: this.filter,
        status: "published",
        page: page,
        per_page: 10,
      };

      this.loadingData = true;

      this.fetchDiscoverCapsules(payload).then(() => {
        this.loadingData = false;

        if (!this.hasRunTour) this.startTour();
        const ids = this.capsules.map((c) => c.id);
        this.fetchVotes(ids);
      });
    },

    startTour() {
      this.$tours["myTour"].start();
    },

    finishTour() {
      if (!this.hasRunTour) {
        const userId = this.currentUser ? this.currentUser.id : null;
        const formData = new FormData();
        formData.append("home_coachmarks", true);

        const payload = {
          userId: userId,
          coachmarksPage: "home",
          formData: formData,
        };

        this.handleFinishFeatureTour(payload);
      }
    },

    featureTourCallback(currentStep) {
      let el = document.querySelector(this.$tours["myTour"].step.target);
      bus.$emit("featureTourTriggerNextSlide", parseInt(el.dataset["layerId"]));
    },

    featureTourMoveToNextStep(currentStep) {
      this.$nextTick(() => {
        if (this.$tours["myTour"].currentStep === currentStep) {
          this.$tours["myTour"].nextStep();
        }
      });
    },
  },

  watch: {
    filter: function (newVal, oldVal) {
      if (newVal !== oldVal) this.requestCapsules(1);
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/_colour-palette.scss";
@import "~@/styles/_breakpoints.scss";

.v-tour__target--highlighted /deep/ {
  box-shadow: 0 0 0 99999px rgba(0, 0, 0, 0.4);
}

.discover-capsules-container {
  .feature-tour-icon {
    cursor: pointer;
    font-size: 24px;
    padding: 10px;
    padding-top: 24px;
  }

  .filters-wrapper {
    display: flex;
    flex-direction: row;
    justify-content: center;
    margin: 50px auto;
    padding: 10px;

    .sidebar-filter-btn {
      cursor: pointer;
      background-color: white;
      min-width: 50px;
      width: 125px;
      margin-bottom: 0;
      padding: 10px 15px;
      opacity: 0.8;

      &:last-child {
        margin-left: 15px;
      }

      &:hover {
        background-color: $grey8;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1),
          0 1px 2px 0 rgba(0, 0, 0, 0.06);
        opacity: 1;

        .icon {
          &.fresh {
            fill: $emerald;
          }

          &.hot {
            fill: $alizarin;
          }
        }
      }

      &.active {
        opacity: 1;
        background-color: $grey8;
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1),
          0 1px 2px 0 rgba(0, 0, 0, 0.06);

        .icon {
          &.fresh {
            fill: $emerald;
          }

          &.hot {
            fill: $alizarin;
          }
        }
      }

      .icon {
        fill: $grey3;
        margin-right: 15px;
        font-size: 16px;
      }

      .text {
        font-size: 14px;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/discover/DiscoverCapsules.vue